import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Music.css';

const formatSize = (bytes) => {
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / 1024 / 1024).toFixed(1) + ' MB';
};

const trackLabel = (name) => name.replace(/\.[^/.]+$/, '');

const loadPlaylist = () => {
  try {
    const raw = localStorage.getItem('music_playlist');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const Music = () => {
  const navigate = useNavigate();
  const [tracks, setTracks]           = useState([]);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [shuffle, setShuffle] = useState(false);
  const [playlist, setPlaylist] = useState(loadPlaylist);
  const [viewingPlaylist, setViewingPlaylist] = useState(false);
  const [creatingPlaylist, setCreatingPlaylist] = useState(false);
  const [pendingSelection, setPendingSelection] = useState([]);
  const volumeRef = useRef(parseFloat(localStorage.getItem('music_volume')) || 0.5);

  useEffect(() => { fetchTracks(); }, []);

  const setAudioRef = (el) => {
    if (el) el.volume = volumeRef.current;
  };

  const handleVolumeChange = (e) => {
    volumeRef.current = e.target.volume;
    localStorage.setItem('music_volume', e.target.volume);
  };

  const fetchTracks = async () => {
    const res = await fetch('/privateZone/music_list.php', { credentials: 'include' });
    if (res.status === 401) { navigate('/login'); return; }
    const data = await res.json();
    setTracks(data.tracks);
  };

  const playlistTracks = playlist
    ? playlist.trackNames.map((n) => tracks.find((t) => t.name === n)).filter(Boolean)
    : [];

  const displayedTracks = (viewingPlaylist && playlist) ? playlistTracks : tracks;

  const playNext = (offset) => {
    if (!currentTrack) return;
    const list = (viewingPlaylist && playlist) ? playlistTracks : tracks;
    if (list.length === 0) return;
    if (shuffle) {
      if (list.length === 1) { setCurrentTrack(list[0]); return; }
      let next;
      do { next = list[Math.floor(Math.random() * list.length)]; } while (next.name === currentTrack.name);
      setCurrentTrack(next);
      return;
    }
    const idx  = list.findIndex((t) => t.name === currentTrack.name);
    const next = list[idx + offset];
    if (next) setCurrentTrack(next);
  };

  const handleTrackClick = (t) => {
    if (creatingPlaylist) {
      setPendingSelection((prev) => (
        prev.includes(t.name) ? prev.filter((n) => n !== t.name) : [...prev, t.name]
      ));
      return;
    }
    setCurrentTrack(t);
  };

  const handlePlaylistButtonClick = () => {
    if (creatingPlaylist) {
      if (pendingSelection.length > 0) {
        const newPlaylist = { name: 'Playlist 1', trackNames: pendingSelection };
        setPlaylist(newPlaylist);
        localStorage.setItem('music_playlist', JSON.stringify(newPlaylist));
      }
      setCreatingPlaylist(false);
      setPendingSelection([]);
      return;
    }
    if (playlist) {
      if (viewingPlaylist) {
        setViewingPlaylist(false);
      } else {
        setViewingPlaylist(true);
        if (playlistTracks.length > 0) setCurrentTrack(playlistTracks[0]);
      }
      return;
    }
    setViewingPlaylist(false);
    setCreatingPlaylist(true);
    setPendingSelection([]);
  };

  const handleDeletePlaylist = () => {
    localStorage.removeItem('music_playlist');
    setPlaylist(null);
    setViewingPlaylist(false);
  };

  const playlistButtonLabel = creatingPlaylist
    ? 'Sauver playlist'
    : (playlist ? playlist.name : 'Créer playlist');

  const handleLogout = async () => {
    await fetch('/privateZone/logout.php', { credentials: 'include' });
    navigate('/login');
  };

  return (
    <div className="music-container">
      <div className="music-header">
        <h2>Musiques</h2>
        <span className="music-count">{displayedTracks.length} piste{displayedTracks.length !== 1 ? 's' : ''}</span>
        <button className="music-logout" onClick={handleLogout} title="Déconnexion">⏻</button>
      </div>

      <div className="music-actions">
        <button
          className={`music-playlist-btn ${viewingPlaylist ? 'active' : ''} ${creatingPlaylist ? 'creating' : ''}`}
          onClick={handlePlaylistButtonClick}
        >
          {playlistButtonLabel}
        </button>
        {playlist && !creatingPlaylist && (
          <button
            className="music-playlist-delete"
            onClick={handleDeletePlaylist}
            title="Supprimer la playlist"
          >
            ✕
          </button>
        )}
        {creatingPlaylist && (
          <span className="music-playlist-hint">
            {pendingSelection.length} sélectionnée{pendingSelection.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <ul className="music-track-list">
        {displayedTracks.length === 0 && <li className="music-empty">Aucune musique disponible</li>}
        {displayedTracks.map((t, i) => (
          <li
            key={t.name}
            className={`music-track-item ${currentTrack?.name === t.name ? 'playing' : ''} ${creatingPlaylist && pendingSelection.includes(t.name) ? 'selected' : ''}`}
            onClick={() => handleTrackClick(t)}
          >
            <span className="music-track-num">{String(i + 1).padStart(2, '0')}</span>
            <span className="music-track-indicator">
              {creatingPlaylist
                ? (pendingSelection.includes(t.name) ? '☑' : '☐')
                : (currentTrack?.name === t.name ? '▶' : '○')}
            </span>
            <span className="music-track-name">{trackLabel(t.name)}</span>
            <span className="music-track-size">{formatSize(t.size)}</span>
          </li>
        ))}
      </ul>

      <div className={`music-player-bar ${currentTrack ? 'visible' : ''}`}>
        <div className="music-player-title">
          {currentTrack ? trackLabel(currentTrack.name) : ''}
        </div>
        <div className="music-player-controls">
          <button
            className={`music-skip music-shuffle ${shuffle ? 'active' : ''}`}
            onClick={() => setShuffle((s) => !s)}
            title="Lecture aléatoire"
          >
            🔀
          </button>
          <button className="music-skip" onClick={() => playNext(-1)} title="Précédent">⏮</button>
          <audio
            key={currentTrack?.name}
            ref={setAudioRef}
            src={currentTrack ? `/privateZone/music_stream.php?file=${encodeURIComponent(currentTrack.name)}` : undefined}
            controls
            autoPlay
            className="music-audio"
            onEnded={() => playNext(1)}
            onVolumeChange={handleVolumeChange}
          />
          <button className="music-skip" onClick={() => playNext(1)} title="Suivant">⏭</button>
        </div>
      </div>
    </div>
  );
};

export default Music;
